/**
 * The Creos' words and marks (Quick Create until 8 Oct), shared by the new
 * chat, the Creos page, the pop-up, the composer's pills and "/". The Creos
 * are every skill and agent: a card is
 * the skill's or agent's own name, description and picture. People never read
 * "skill" or "agent": a card says how it runs (Works with you, Runs on its own).
 * One message can carry several picks, agents included.
 */
import { Bot, ScanSearch, type LucideIcon } from "lucide-react";
import type { WorkshopQuickCreateItem, WorkshopQuickCreateRef } from "@/types/workshop.type";

/** How many tiles lead the new chat before All. */
export const QUICK_CREATE_FEATURED = 4;

/** Most picks one message carries; agents also fit one spend check (backend caps). */
export const QUICK_CREATE_MAX_PICKS = 10;
export const QUICK_CREATE_MAX_AGENTS = 5;

export function kindLabel(kind: WorkshopQuickCreateItem["kind"]): string {
  return kind === "agent" ? "Runs on its own" : "Works with you";
}

/** The mark of a card without a picture, and of each "/" row. */
export function kindIcon(kind: WorkshopQuickCreateItem["kind"]): LucideIcon {
  return kind === "agent" ? Bot : ScanSearch;
}

/** The pop-up's HOW: how it runs. */
export function howItRuns(item: WorkshopQuickCreateItem): string {
  return item.kind === "agent"
    ? "Runs on its own. You keep working, and it files a document in Outputs. It spends from the AI budget."
    : "Works with you in the chat.";
}

/**
 * The tiles that lead the new chat and the dashboard: the first featured cards
 * in list order, or the first cards when none is featured. The list is already
 * this person's (a guest's has no agents), so a featured card they can't use
 * never takes a slot.
 */
export function featuredItems(items: WorkshopQuickCreateItem[]): WorkshopQuickCreateItem[] {
  const featured = items.filter((item) => item.featured);
  return (featured.length > 0 ? featured : items).slice(0, QUICK_CREATE_FEATURED);
}

/** The row for cards without a category, always last. */
export const CREO_OTHER_CATEGORY = "Other";

/**
 * The Creos page's streams: one row per category, A to Z, skills and agents
 * together; cards without one go under Other at the end. Rows are matched on
 * the trimmed name, case-insensitively, so "strategy" joins "Strategy".
 */
export function creoStreams(
  items: WorkshopQuickCreateItem[],
): { key: string; label: string; items: WorkshopQuickCreateItem[] }[] {
  const rows = new Map<string, { key: string; label: string; items: WorkshopQuickCreateItem[] }>();
  for (const item of items) {
    const label = item.category?.trim() || CREO_OTHER_CATEGORY;
    const key = label.toLowerCase();
    const row = rows.get(key) ?? { key, label, items: [] };
    row.items.push(item);
    rows.set(key, row);
  }
  const other = CREO_OTHER_CATEGORY.toLowerCase();
  return [...rows.values()].sort((a, b) =>
    a.key === other ? 1 : b.key === other ? -1 : a.label.localeCompare(b.label),
  );
}

/** Matches by name first, then (unless `namesOnly`, as "/" is) by its description. */
export function matchQuickCreate(
  items: WorkshopQuickCreateItem[],
  query: string,
  namesOnly = false,
): WorkshopQuickCreateItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const byName = items.filter((item) => item.name.toLowerCase().includes(q));
  if (namesOnly) return byName;
  const rest = items.filter((item) => !byName.includes(item) && item.description.toLowerCase().includes(q));
  return [...byName, ...rest];
}

/** One pick's identity: a skill and an agent may share a key. */
export function pickId(ref: Pick<WorkshopQuickCreateRef, "kind" | "key">): string {
  return `${ref.kind}:${ref.key}`;
}

export function quickCreateRef(item: WorkshopQuickCreateItem): WorkshopQuickCreateRef {
  return { kind: item.kind, key: item.key, id: item.id, name: item.name, imageUrl: item.imageUrl };
}

/** Room for this card among the picks: not in yet, and under both caps. */
export function canAddPick(picks: WorkshopQuickCreateRef[], item: WorkshopQuickCreateItem): boolean {
  if (picks.some((p) => pickId(p) === pickId(item))) return false;
  if (picks.length >= QUICK_CREATE_MAX_PICKS) return false;
  if (item.kind === "agent" && picks.filter((p) => p.kind === "agent").length >= QUICK_CREATE_MAX_AGENTS) {
    return false;
  }
  return true;
}

/** The picks with this card added (unchanged when it has no room). */
export function addPick(picks: WorkshopQuickCreateRef[], item: WorkshopQuickCreateItem): WorkshopQuickCreateRef[] {
  return canAddPick(picks, item) ? [...picks, quickCreateRef(item)] : picks;
}

export function removePick(picks: WorkshopQuickCreateRef[], ref: WorkshopQuickCreateRef): WorkshopQuickCreateRef[] {
  return picks.filter((p) => pickId(p) !== pickId(ref));
}

/** The picks that went while they sat in a draft: refused on send, or gone from the list. */
export function unavailablePicks(
  picks: WorkshopQuickCreateRef[],
  items: WorkshopQuickCreateItem[] | undefined,
  refused: string[],
): WorkshopQuickCreateRef[] {
  return picks.filter(
    (p) => refused.includes(pickId(p)) || (items !== undefined && !items.some((i) => pickId(i) === pickId(p))),
  );
}

/** What a send carries to the API. */
export function quickCreatePayload(picks: WorkshopQuickCreateRef[]): { kind: string; key: string }[] {
  return picks.map(({ kind, key }) => ({ kind, key }));
}

function readRef(value: unknown): WorkshopQuickCreateRef | null {
  if (!value || typeof value !== "object") return null;
  const ref = value as Partial<WorkshopQuickCreateRef>;
  if (typeof ref.key !== "string" || typeof ref.name !== "string") return null;
  return {
    kind: ref.kind === "agent" ? "agent" : "skill",
    key: ref.key,
    id: typeof ref.id === "string" ? ref.id : undefined,
    name: ref.name,
    imageUrl: typeof ref.imageUrl === "string" ? ref.imageUrl : null,
  };
}

/**
 * The pills a sent message carries (metadata.quickCreates). Messages sent before
 * several could be picked kept one, under metadata.quickCreate.
 */
export function messageQuickCreates(metadata: Record<string, unknown> | null | undefined): WorkshopQuickCreateRef[] {
  const many = metadata?.quickCreates;
  if (Array.isArray(many)) return many.map(readRef).filter((r): r is WorkshopQuickCreateRef => r !== null);
  const one = readRef(metadata?.quickCreate);
  return one ? [one] : [];
}

/** The API refused a pick: it went while it sat in a draft. */
export const QUICK_CREATE_UNAVAILABLE = "QUICK_CREATE_UNAVAILABLE";

export function unavailableNote(names: string[]): string {
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  return `${list} ${names.length === 1 ? "is" : "are"} no longer available. Remove ${names.length === 1 ? "it" : "them"} to send your words as a message.`;
}

/**
 * The API refused a send because a pick is gone (409 QUICK_CREATE_UNAVAILABLE):
 * that pick's id, or "*" when the answer does not say which.
 */
export function refusedPick(error: unknown): string | null {
  const response = (error as { response?: { status?: number; data?: { code?: string; kind?: string; key?: string } } })
    ?.response;
  if (response?.status !== 409 || response.data?.code !== QUICK_CREATE_UNAVAILABLE) return null;
  const { kind, key } = response.data;
  return kind && key ? pickId({ kind: kind === "agent" ? "agent" : "skill", key }) : "*";
}

/** Marks the refused pick (or, when unnamed, every pick) as gone. */
export function refusedIds(refused: string, picks: WorkshopQuickCreateRef[]): string[] {
  return refused === "*" ? picks.map(pickId) : [refused];
}
